import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CheckoutReviewAndSubmitPageContext } from "@insite/content-library/Pages/CheckoutReviewAndSubmitPage";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import setCustomerReference3Error from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/CustomerReference3ErrorHandler";
import setcustomerReference3 from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/CustomerReference3Handler";

import { TYPOGRAPHY } from "@tKgpco/theme/design";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomApplicationState) => {
    const cartState = getCurrentCartState(state).value;

    return {
        cartState,
        currentBillTo: getBillToState(state, cartState?.billToId).value,
        customerReference3Error: state.KGPState.CheckoutReview.customerReference3Error,
        customerReference3: state.KGPState.CheckoutReview.customerReference3,
        isAwaitingApproval: cartState?.isAwaitingApproval,
    };
};

const mapDispatchToProps = {
    setcustomerReference3,
    setCustomerReference3Error,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutShippingCarrierServiceStyles {
    customerReferenceGrid?: GridItemProps;
    customerReferenceChildGrid?: GridItemProps;
    customerReferenceSelect?: SelectPresentationProps;
    customerOrderReferenceLabelStyle?: InjectableCss;
}

export const checkoutShippingCarrierServiceStyles: CheckoutShippingCarrierServiceStyles = {
    customerReferenceGrid: {
        width: 12,
        css: css`
            padding: 0px 5px 3px 16px;
            label {
                font-size: ${TYPOGRAPHY.size.smallest};
            }
        `,
    },
    customerReferenceChildGrid: {
        width: 12,
        css: css`
            padding: 0px 5px 20px 16px;
            label {
                font-size: ${TYPOGRAPHY.size.smallest};
            }
        `,
    },
    customerOrderReferenceLabelStyle: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smallest};
            font-weight: 600;
        `,
    },
};

export const checkoutShippingCarrierService = checkoutShippingCarrierServiceStyles;
const styles = checkoutShippingCarrierServiceStyles;

const CustomerReference3Field: FC<Props> = ({
    setcustomerReference3,
    currentBillTo,
    customerReference3,
    customerReference3Error,
    setCustomerReference3Error,
    isAwaitingApproval,
    cartState,
}) => {
    const [properties, setProperties] = useState(currentBillTo?.properties);
    const [customerRefChild, setCustomerRefChild] = useState(customerReference3 || "");
    const [customerReference3Val, setCustomerReference3Val] = useState(customerReference3 || "");
    const [customerRefError, setCustomerRefError] = useState(
        properties?.customerOrderReferenceRequired3?.toLowerCase() === "true" ? customerReference3Error : "",
    );

    useEffect(() => {
        properties?.customerOrderReferenceRequired3?.toLowerCase() === "true" &&
            setCustomerRefError(customerReference3Error);
    }, [customerReference3Error]);

    useEffect(() => {
        setProperties(currentBillTo?.properties);
    }, [currentBillTo?.properties]);

    useEffect(() => {
        setcustomerReference3({ customerReference3: customerReference3Val || "" });
    }, [customerReference3Val]);

    useEffect(() => {
        setcustomerReference3({ customerReference3: customerRefChild || "" });
    }, [customerRefChild]);

    useEffect(() => {
        if (
            cartState?.properties &&
            cartState.properties?.customerOrderRefSelectedValue3 &&
            cartState.properties?.customerOrderRefSelectedValue3.split("|").length > 1
        ) {
            setCustomerReference3Val(cartState.properties.customerOrderRefSelectedValue3.split("|")[1]);
            setCustomerRefChild(cartState.properties.customerOrderRefSelectedValue3.split("|")[1]);
        }
    }, [cartState?.properties]);

    const customerReferenceDropDownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const error =
            e.target.value.trim() === ""
                ? `${siteMessage(properties?.customerOrderReferenceLabel3 ?? "")} is required`
                : "";

        setCustomerRefChild(e.target.value);
        setcustomerReference3({ customerReference3: e.target.value });
        if (properties?.customerOrderReferenceRequired3?.toLowerCase() === "true") {
            setCustomerReference3Error({ customerReference3Error: error });
        }
    };

    const customerReferenceInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const error =
            e.target.value.trim() === ""
                ? `${siteMessage(properties?.customerOrderReferenceLabel3 ?? "")} is required`
                : "";

        setCustomerReference3Val(e.target.value);
        setcustomerReference3({ customerReference3: e.target.value });
        if (properties?.customerOrderReferenceRequired3?.toLowerCase() === "true") {
            setCustomerReference3Error({ customerReference3Error: error });
        }
    };

    if (!properties?.customerOrderReferenceType3 || properties?.customerOrderReferenceAttribute3 === "None") {
        return null;
    }

    return (
        <GridContainer>
            <GridItem {...styles.customerReferenceChildGrid}>
                {properties?.customerOrderReferenceType3 === "Dropdown" ? (
                    <Select
                        {...styles.customerReferenceSelect}
                        label={properties.customerOrderReferenceLabel3}
                        value={customerRefChild}
                        onChange={customerReferenceDropDownChange}
                        ref={null}
                        required={properties?.customerOrderReferenceRequired3?.toLowerCase() === "true"}
                        error={
                            properties?.customerOrderReferenceRequired3?.toLowerCase() === "true" && customerRefError
                        }
                        disabled={isAwaitingApproval}
                    >
                        <option value="">{translate("Select option")}</option>
                        {properties?.listReferencesValues3.split("|").map(method => (
                            <option key={method} value={method}>
                                {method}
                            </option>
                        ))}
                    </Select>
                ) : (
                    <TextField
                        label={properties?.customerOrderReferenceLabel3}
                        value={customerReference3Val}
                        onChange={customerReferenceInputChange}
                        required={properties?.customerOrderReferenceRequired3?.toLowerCase() === "true"}
                        error={
                            properties?.customerOrderReferenceRequired3?.toLowerCase() === "true" && customerRefError
                        }
                        disabled={isAwaitingApproval}
                    />
                )}
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CustomerReference3Field),
    definition: {
        displayName: "Customer Reference 3 Field",
        group: "Checkout - Review & Submit",
        allowedContexts: [CheckoutReviewAndSubmitPageContext],
    },
};

export default widgetModule;
